'use client';

import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { Box, IconButton, Typography } from '@mui/material';

import { LiveBadge } from '@/components';
import { formatMarketResolutionDate, formatVolume } from '@/utils/formatters';

interface MarketCardFooterProps {
  volume: number;
  isBookmarked: boolean;
  onBookmarkClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isResolved?: boolean;
  endDate?: string;
  isLive?: boolean;
  alwaysShowBookmark?: boolean;
}

export function MarketCardFooter({
  volume,
  isBookmarked,
  onBookmarkClick,
  isResolved,
  endDate,
  isLive,
  alwaysShowBookmark = false,
}: MarketCardFooterProps) {
  const formatedVol = formatVolume(volume);
  const formattedEndDate = formatMarketResolutionDate(endDate);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mt: 'auto',
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Live Badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isLive && <LiveBadge />}

          <Typography
            variant="body2"
            sx={{
              fontSize: '0.9375rem',
              color: 'text.primary',
              fontWeight: 600,
            }}
          >
            {!isLive && `${formatedVol} Vol.`}
          </Typography>
        </Box>
        <IconButton
          size="small"
          sx={{
            color: 'text.secondary',
            p: 0.5,
            '&:hover': {
              color: 'text.primary',
            },
          }}
          aria-label="Refresh"
        >
          <Box
            component="svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
            <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
            <path d="M16 21h5v-5" />
          </Box>
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isResolved && endDate && (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {formattedEndDate}
          </Typography>
        )}

        {(!isResolved || alwaysShowBookmark) && (
          <IconButton
            size="small"
            onClick={onBookmarkClick}
            sx={{
              color: isBookmarked ? 'primary.main' : 'text.secondary',
              p: 0.5,
              transition: 'color 0.2s ease',
              '&:hover': {
                color: isBookmarked ? 'primary.dark' : 'text.primary',
              },
            }}
            aria-label="Bookmark"
          >
            {isBookmarked ? (
              <BookmarkIcon sx={{ fontSize: '1.25rem' }} />
            ) : (
              <BookmarkBorderIcon sx={{ fontSize: '1.25rem' }} />
            )}
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
